import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import i18n from 'i18next';

export type BannerState = 'prompt' | 'waiting' | 'granted' | 'denied';

interface NotificationBannerContextValue {
  shouldShowBanner: boolean;
  bannerState: BannerState;
  requestPermission: () => Promise<void>;
  dismiss: () => void;
}

const defaultContext: NotificationBannerContextValue = {
  shouldShowBanner: false,
  bannerState: 'prompt',
  requestPermission: async () => {},
  dismiss: () => {},
};

export const NotificationBannerCtx = createContext<NotificationBannerContextValue>(defaultContext);
export const useNotificationBanner = () => useContext(NotificationBannerCtx);

const NOTIFICATION_DISMISSED_KEY = 'bops-agent-notification-dismissed';

/** Safe sessionStorage read — returns null when storage is unavailable (e.g. Safari private mode). */
const safeGetItem = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

/** Safe sessionStorage write — silently fails when storage is unavailable or full. */
const safeSetItem = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable or quota exceeded — ignore
  }
};

const AUTO_DISMISS_MS = 4000;

/**
 * Hook for managing browser notifications in the Agent chat.
 *
 * Follows the double opt-in pattern:
 * 1. An inline banner asks the user if they want notifications
 * 2. Only if they click "Enable" does the native browser prompt appear
 *
 * Notifications are only sent when the tab is not visible or the window is not focused.
 * The hook internally sends a notification when thinking completes.
 *
 * Returns a context value — provide it via NotificationBannerCtx.Provider so
 * NotificationBanner can consume it without prop drilling.
 */
const useBrowserNotification = (isThinking: boolean): NotificationBannerContextValue => {
  const isSupported = typeof window !== 'undefined' && 'Notification' in window;

  const [permission, setPermission] = useState<NotificationPermission>(() => {
    if (!isSupported) return 'default';
    return Notification.permission;
  });

  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return safeGetItem(NOTIFICATION_DISMISSED_KEY) === 'true';
  });

  const [isEnabled, setIsEnabled] = useState<boolean>(permission === 'granted');
  const [bannerState, setBannerState] = useState<BannerState>(() => (permission === 'denied' ? 'denied' : 'prompt'));
  const activeNotificationRef = useRef<Notification | null>(null);
  const autoDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasThinkingRef = useRef(false);

  // Sync permission state if it changes externally
  useEffect(() => {
    if (!isSupported) return;
    setPermission(Notification.permission);
    setIsEnabled(Notification.permission === 'granted');
  }, [isSupported]);

  // Cleanup auto-dismiss timer on unmount
  useEffect(() => {
    return () => {
      if (autoDismissTimerRef.current) clearTimeout(autoDismissTimerRef.current);
    };
  }, []);

  // Auto-dismiss banner after granted/denied result
  const scheduleAutoDismiss = useCallback(() => {
    if (autoDismissTimerRef.current) clearTimeout(autoDismissTimerRef.current);
    autoDismissTimerRef.current = setTimeout(() => {
      setIsDismissed(true);
      safeSetItem(NOTIFICATION_DISMISSED_KEY, 'true');
    }, AUTO_DISMISS_MS);
  }, []);

  // Send browser notification when thinking completes
  useEffect(() => {
    if (wasThinkingRef.current && !isThinking && isEnabled && isSupported && permission === 'granted') {
      // Only notify when the tab is not visible or the window is not focused
      if (document.hidden || typeof document.hasFocus !== 'function' || !document.hasFocus()) {
        if (activeNotificationRef.current) {
          activeNotificationRef.current.close();
        }

        const notification = new Notification(i18n.t('Agent.NotificationTitle'), {
          body: i18n.t('Agent.NotificationBody'),
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'bops-agent-response',
        });

        activeNotificationRef.current = notification;

        notification.onclick = () => {
          window.focus();
          notification.close();
          activeNotificationRef.current = null;
        };

        setTimeout(() => {
          notification.close();
          if (activeNotificationRef.current === notification) {
            activeNotificationRef.current = null;
          }
        }, 8000);
      }
    }
    wasThinkingRef.current = isThinking;
  }, [isThinking, isEnabled, isSupported, permission]);

  const requestPermission = useCallback(async () => {
    if (!isSupported) return;

    setBannerState('waiting');

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      setIsEnabled(result === 'granted');
      setBannerState(result === 'granted' ? 'granted' : 'denied');
      scheduleAutoDismiss();
    } catch {
      setBannerState('denied');
      scheduleAutoDismiss();
    }
  }, [isSupported, scheduleAutoDismiss]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    safeSetItem(NOTIFICATION_DISMISSED_KEY, 'true');
  }, []);

  // Show banner when thinking + in a visible state
  const isResultState = bannerState === 'granted' || bannerState === 'denied' || bannerState === 'waiting';
  const shouldShowBanner = isThinking && isSupported && !isDismissed && (isResultState || !isEnabled);

  return {
    shouldShowBanner,
    bannerState,
    requestPermission,
    dismiss,
  };
};

export default useBrowserNotification;
